import { useSelector } from "react-redux";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Stack,
  Typography,
} from "@financeable-com-au/financeable-ui";
import { AuthCard } from "@components/AuthCard";
import { themeSelector } from "@/store/slices/themeSlice";

interface EmailStepProps {
  onContinue: (email: string) => void;
}

const EmailStep = ({ onContinue }: EmailStepProps) => {
  const { theme } = useSelector(themeSelector);

  const isCustomThemed = !!theme;

  const formik = useFormik({
    initialValues: {
      email: "",
    },
    onSubmit: (values) => {
      onContinue(values.email.trim());
    },
    validationSchema: yup.object({
      email: yup.string().trim().required("Please enter your email.").email("Please enter a valid email address."),
    }),
    validateOnChange: false,
    validateOnBlur: false,
  });

  return (
    <>
      {/* Welcome statement */}
      <Stack textAlign="center" rowGap={1}>
        <Typography level="h2" gradient={isCustomThemed ? "primary" : "default"}>
          Welcome Back!
        </Typography>
        <Typography level="body-lg" textColor="neutral.600">
          Sign in to your account below.
        </Typography>
      </Stack>
      {/* Action sheet */}
      <AuthCard onSubmit={() => formik.handleSubmit()}>
        <Stack rowGap={6}>
          <FormControl error={!!formik.errors.email}>
            <FormLabel size="md">Email</FormLabel>
            <Input
              autoFocus
              placeholder="Your email address"
              type="email"
              value={formik.values.email}
              onChange={(event) => {
                formik.setFieldValue("email", event.target.value);
                if (formik.errors.email) {
                  formik.setFieldError("email", undefined);
                }
              }}
              size="lg"
            />
            {formik.errors.email ? (
              <FormHelperText>
                <ErrorOutlineIcon sx={{ fontSize: "1rem" }} />
                {formik.errors.email}
              </FormHelperText>
            ) : null}
          </FormControl>
          <Button
            size="lg"
            variant="primary"
            endDecorator={<ChevronRightIcon sx={{ fontSize: "1.2rem" }} />}
            type="submit"
          >
            Continue
          </Button>
        </Stack>
      </AuthCard>
    </>
  );
};

export { EmailStep };
